import React, { useState } from "react";
import { Box, Chip } from "@mui/material";
import dayjs from "dayjs";
import LGModalCrud from "../LGCrud/LGModalCrud";
import {
  deleteScheduledPayment,
  exportScheduledPaymentLayout,
  generateScheduledPayments,
  getScheduledPaymentList,
} from "../../api/scheduledPayments";
import { currencyFormatterMXN } from "../../services/valueFormatters";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import ScheduledPaymentForm from "./ScheduledPaymentForm";
import ScheduledPaymentLinkItemsModal from "./ScheduledPaymentLinkItemsModal";
import ScheduledPaymentItemsView from "./ScheduledPaymentItemsView";
import DateFilterModal from "../ReceiptAcknowledgement/DateFilterModal";

const setStatusColor = (statusCode) => {
  switch (statusCode) {
    case "SP_DRAFT":
      return "default";
    case "SP_ORPHAN":
      return "warning";
    case "SP_SCHEDULED":
      return "info";
    case "SP_EXPORTED":
      return "primary";
    case "SP_CLOSED":
      return "success";
    case "SP_CANCELLED":
      return "error";
    default:
      return "default";
  }
};

const getCurrentYearMonthCode = () => {
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, "0");
  return `${year}${month}`;
};

export default function ScheduledPaymentCrud({ props }) {
  const { companyId } = props;
  const { openSnack } = useSnack();
  const [gridKey, setGridKey] = useState(0);
  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [dateCode, setDateCode] = useState(getCurrentYearMonthCode);

  const handleDateFilter = (date) => {
    if (!date) {
      setDateCode(getCurrentYearMonthCode());
      return;
    }
    const year = date.year().toString().slice(-2);
    const month = (date.month() + 1).toString().padStart(2, "0");
    setDateCode(`${year}${month}`);
  };

  const handleExportLayout = async (selectedRows) => {
    if (selectedRows.length === 0) {
      openSnack("Selecciona al menos un pago", "warning");
      return;
    }
    try {
      const layout = await exportScheduledPaymentLayout(selectedRows);
      const fileName = `poliza_egreso_${dayjs().format("YYYYMMDD_HHmmss")}.txt`;

      const url = window.URL.createObjectURL(new Blob([layout.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setGridKey((key) => key + 1);
    } catch (error) {
      openSnack(handleError(error) || "Error al exportar layout", "error");
    }
  };

  const handleGeneratePayment = async (selectedRows) => {
    if (selectedRows.length === 0) {
      openSnack("Selecciona al menos un pago", "warning");
      return;
    }
    try {
      const result = await generateScheduledPayments(selectedRows);
      const created = result.data?.paymentTxsCreated ?? selectedRows.length;
      openSnack(
        created === 1
          ? "Pago generado exitosamente"
          : `${created} pagos generados exitosamente`,
        "success"
      );
      setGridKey((key) => key + 1);
    } catch (error) {
      openSnack(handleError(error) || "Error al generar pago", "error");
    }
  };

  return (
    <Box width={"100%"}>
      <DateFilterModal
        open={dateFilterOpen}
        handleClose={() => setDateFilterOpen(false)}
        onSubmit={handleDateFilter}
      />
      <LGModalCrud
        key={gridKey}
        props={{
          actions: ["create", "delete", "detail", "select"],
          data: {
            companyId,
            sortField: "code",
            sortDir: "desc",
            codePrefix: `P${dateCode}`,
          },
          fetchFunction: getScheduledPaymentList,
          deleteFunction: deleteScheduledPayment,
          customButtons: [
            {
              label: "Filtrar por Mes/Año",
              icon: "calendar_month",
              color: "primary",
              onClick: () => setDateFilterOpen(true),
            },
            {
              label: "Exportar Layout",
              icon: "file_download",
              color: "secondary",
              onClick: handleExportLayout,
              showOnlyWithSelection: true,
            },
            {
              label: "Generar pago",
              icon: "price_check",
              color: "primary",
              onClick: handleGeneratePayment,
              showOnlyWithSelection: true,
            },
          ],
          detailMenu: (row) => [
            ...(row.status?.code === "SP_ORPHAN"
              ? [
                  {
                    label: "Vincular con Contrarrecibos",
                    header: "Vincular con Contrarrecibos",
                    body: <ScheduledPaymentLinkItemsModal />,
                  },
                ]
              : []),
            {
              label: "Ver renglones",
              header: "Renglones",
              body: <ScheduledPaymentItemsView />,
            },
          ],
          columns: [
            {
              field: "code",
              headerName: "Folio",
              flex: 0.2,
              filterable: true,
            },
            {
              field: "provider",
              headerName: "Proveedor",
              flex: 0.2,
              filterable: true,
              valueGetter: (value, row) =>
                row.provider ? `${row.provider.code}` : "",
            },
            {
              field: "paymentDate",
              headerName: "Fecha de Pago",
              flex: 0.2,
              type: "date",
              filterable: true,
              valueGetter: (value, row) =>
                row?.paymentDate ? dayjs(row.paymentDate).toDate() : null,
              valueFormatter: (value) =>
                value != null ? dayjs(value).format("DD/MM/YYYY") : "",
            },
            {
              field: "currency",
              headerName: "Moneda",
              flex: 0.1,
              filterable: true,
              valueGetter: (value, row) =>
                row.currency ? `${row.currency.symbol}` : "",
            },
            {
              field: "totalAmount",
              headerName: "Monto Total",
              flex: 0.2,
              type: "number",
              filterable: true,
              ...currencyFormatterMXN,
            },
            {
              field: "status",
              headerName: "Estatus",
              flex: 0.2,
              filterable: true,
              valueGetter: (value, row) => row.status?.name || "",
              renderCell: (params) => (
                <Box
                  mt={2}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Chip
                    size="small"
                    label={params.row.status.name.toUpperCase()}
                    color={setStatusColor(params.row.status.code)}
                  />
                </Box>
              ),
            },
          ],
          addModalData: {
            header: "Nueva Programación de Pago",
            body: <ScheduledPaymentForm companyId={companyId} />,
          },
          dataGridProps: {
            initialState: {
              sorting: {
                sortModel: [{ field: "code", sort: "desc" }],
              },
            },
          },
        }}
      />
    </Box>
  );
}
